/**
 * Copy text to the clipboard (`container`: the element to host the fallback
 * textarea, e.g. an open dialog). Resolves `true` on success, `false` when the
 * browser refused (no permission, insecure context) — never throws, so a
 * caller can show "Copied" or "Select and copy it manually" without a try.
 *
 * The one shared helper for new code (#793). About seventeen components still
 * carry their own `navigator.clipboard.writeText` copy; moving them here is a
 * mechanical follow-up (plan F8), kept out of the Developer API work because
 * several of those files own their own required docs.
 */
export async function copyToClipboard(text, container) {
    const value = String(text ?? '');
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return true;
        }
    } catch {
        // fall through to the textarea path
    }
    try {
        const area = document.createElement('textarea');
        area.value = value;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        // Inside a modal, pass the dialog element: a focus trap moves focus back
        // out of a textarea appended to <body>, and the copy then copies nothing.
        (container || document.body).appendChild(area);
        area.select();
        const ok = document.execCommand ? document.execCommand('copy') : false;
        area.remove();
        return Boolean(ok);
    } catch {
        return false;
    }
}
